import type { ICorridor } from "../shared/session/interfaces/session";

export interface CorridorPair {
  originCountryCode: string;
  destinationCountryCode: string;
}

/** Unique origin country codes from the link corridors (sorted). */
export const supportedOrigins = (corridors: ICorridor[]): string[] =>
  Array.from(new Set(corridors.map((c) => c.originCountryCode))).sort();

/** Destination codes reachable from the given origin (sorted). */
export const supportedDestinations = (
  corridors: ICorridor[],
  originCountryCode: string
): string[] =>
  Array.from(
    new Set(
      corridors
        .filter((c) => c.originCountryCode === originCountryCode)
        .map((c) => c.destinationCountryCode)
    )
  ).sort();

/**
 * Resolves the initial corridor from session config: pinned default, or the
 * first row in `config.corridors` (widget catalog order).
 */
export const resolveInitialCorridor = (
  corridors: ICorridor[],
  defaultCorridor?: CorridorPair
): CorridorPair | undefined => {
  if (defaultCorridor) return defaultCorridor;
  const first = corridors[0];
  if (!first) return undefined;
  return {
    originCountryCode: first.originCountryCode,
    destinationCountryCode: first.destinationCountryCode,
  };
};

/** Destination country seed when compose store is empty. */
export const seedDestinationCountry = (
  corridors: ICorridor[],
  defaultCorridor?: CorridorPair
): string | undefined =>
  resolveInitialCorridor(corridors, defaultCorridor)?.destinationCountryCode;

/** Origin country seed when compose store is empty. */
export const seedOriginCountry = (
  corridors: ICorridor[],
  defaultCorridor?: CorridorPair
): string | undefined =>
  resolveInitialCorridor(corridors, defaultCorridor)?.originCountryCode;

/** Lookup a single corridor row for an origin/destination pair. */
export const findCorridor = (
  corridors: ICorridor[],
  originCountryCode: string,
  destinationCountryCode: string
): ICorridor | null =>
  corridors.find(
    (c) =>
      c.originCountryCode === originCountryCode &&
      c.destinationCountryCode === destinationCountryCode
  ) ?? null;
